import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../utils/constants";

export default function AdminCustomerDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [customer, setCustomer] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [actioning, setActioning] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const showNotification = (type, text, duration = 3500) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), duration);
  };

  const hasPermission = (permId) => userPermissions.includes(permId);

  const fetchCustomer = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${CONSTANTS.baseRoute}/customers/${id}`);
      setCustomer(data);
    } catch (error) {
      showNotification(
        "danger",
        error.response?.data?.error || t("customers.fetchError")
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  useEffect(() => {
    const init = async () => {
      try {
        const userCookie = Cookies.get("user");
        if (!userCookie) return navigate("/admin");

        const user = JSON.parse(userCookie);
        if (!user?.id) return navigate("/admin");

        const { data: { permissions } } = await axios.get(
          `${CONSTANTS.baseRoute}/permissions`,
          { params: { user_id: user.id } }
        );
        setUserPermissions(permissions);

        if (!permissions.includes(36)) {
          setMessage({ type: "danger", text: t("customers.noPermissionView") });
          setTimeout(() => navigate("/admin/home"), 2500);
          return;
        }

        await fetchCustomer();
      } catch (error) {
        console.error("Error fetching customer:", error);
        if ([401, 403].includes(error.response?.status)) navigate("/admin");
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, id]);

  const handleApprove = async () => {
    if (!hasPermission(38)) return showNotification("danger", t("customers.noPermissionAction"));
    setActioning(true);
    try {
      await axios.put(`${CONSTANTS.baseRoute}/customers/${id}/approve`);
      await fetchCustomer();
      showNotification("success", t("customers.approveSuccess"));
    } catch (error) {
      showNotification("danger", error.response?.data?.error || t("customers.actionError"));
    } finally {
      setActioning(false);
    }
  };

  const handleReject = async () => {
    if (!hasPermission(38)) return showNotification("danger", t("customers.noPermissionAction"));
    if (!window.confirm(t("customers.confirmReject"))) return;

    setActioning(true);
    try {
      await axios.put(`${CONSTANTS.baseRoute}/customers/${id}/reject`);
      await fetchCustomer();
      showNotification("success", t("customers.rejectSuccess"));
    } catch (error) {
      showNotification("danger", error.response?.data?.error || t("customers.actionError"));
    } finally {
      setActioning(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!hasPermission(38)) return showNotification("danger", t("customers.noPermissionAction"));

    if (newPassword.length < 6) {
      return showNotification("danger", t("customers.passwordMinLength"));
    }
    if (newPassword !== confirmPassword) {
      return showNotification("danger", t("customers.passwordMismatch"));
    }

    setPasswordSaving(true);
    try {
      await axios.put(`${CONSTANTS.baseRoute}/customers/${id}/password`, { password: newPassword });
      showNotification("success", t("customers.passwordUpdateSuccess"));
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      showNotification("danger", error.response?.data?.error || t("customers.passwordUpdateError"));
    } finally {
      setPasswordSaving(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { cls: "status-pending", text: t("customers.statusPending") },
      approved: { cls: "status-approved", text: t("customers.statusApproved") },
      rejected: { cls: "status-rejected", text: t("customers.statusRejected") },
    };
    const info = map[status] || { cls: "status-pending", text: status };
    return <span className={`status-pill ${info.cls}`}>{info.text}</span>;
  };

  if (loading) {
    return (
      <div className="admin-main p-4 d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="admin-main ps-2 pe-3 py-3">
        <div className="d-flex gap-2 align-items-center mb-4">
          <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <h4 className="admin-page-title">{t("customers.details.title")}</h4>
        </div>
        {message.text && (
          <div className={`alert alert-${message.type}`} role="alert">
            {message.text}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="admin-main ps-2 pe-3 py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex gap-2 align-items-center">
          <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn admin-icon-btn" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h4 className="admin-page-title">{t("customers.details.title")}</h4>
        </div>

        <div className="d-flex gap-2">
          {customer.status !== "approved" && (
            <button
              className="btn btn-sm btn-outline-success shadow-sm"
              onClick={handleApprove}
              disabled={actioning}
            >
              <i className="bi bi-check-lg me-1"></i>
              {t("customers.approve")}
            </button>
          )}
          {customer.status !== "rejected" && (
            <button
              className="btn btn-sm btn-outline-warning shadow-sm"
              onClick={handleReject}
              disabled={actioning}
            >
              <i className="bi bi-x-lg me-1"></i>
              {t("customers.reject")}
            </button>
          )}
        </div>
      </div>

      {message.text && (
        <div
          className={`alert alert-${message.type} shadow-sm mb-4 alert-dismissible fade show`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage({ type: "", text: "" })}
          ></button>
        </div>
      )}

      <div className="row g-4">
        {/* Müşderi maglumaty */}
        <div className="col-lg-4">
          <div className="admin-card overflow-hidden">
            <div className="admin-card-header">
              <i className="bi bi-person-vcard me-2"></i>
              {t("customers.details.info")}
            </div>
            <div className="card-body d-flex flex-column align-items-center text-center p-4">
              {customer.profile_image ? (
                <img
                  src={`${CONSTANTS.baseRoute}${customer.profile_image}`}
                  alt={customer.name || "Customer"}
                  style={{ width: "84px", height: "84px", objectFit: "cover", borderRadius: "50%" }}
                />
              ) : (
                <i className="bi bi-person-circle" style={{ fontSize: "84px", color: "#dcdcdc" }}></i>
              )}

              <h5 className="fw-bold mt-3 mb-1">{customer.name || "—"}</h5>
              <div className="mb-3">{statusBadge(customer.status)}</div>

              <div className="w-100 text-start">
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="text-muted">{t("customers.phone")}</span>
                  <span className="fw-medium">{customer.phone || "—"}</span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="text-muted">{t("customers.email")}</span>
                  <span className="fw-medium">{customer.email || "—"}</span>
                </div>
                <div className="d-flex justify-content-between py-2">
                  <span className="text-muted">{t("customers.registeredAt")}</span>
                  <span className="fw-medium">
                    {customer.created_at
                      ? new Date(customer.created_at).toLocaleDateString()
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {hasPermission(38) && (
            <div className="admin-card overflow-hidden mt-4">
              <div className="admin-card-header">
                <i className="bi bi-key me-2"></i>
                {t("customers.changePassword")}
              </div>
              <form className="card-body" onSubmit={handleChangePassword}>
                <div className="mb-3">
                  <label className="form-label">{t("customers.newPassword")}</label>
                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                    </button>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">{t("customers.confirmPassword")}</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-success w-100 fw-bold shadow-sm"
                  disabled={passwordSaving || !newPassword || !confirmPassword}
                >
                  {passwordSaving ? t("common.loading") : t("customers.changePassword")}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Sargytlary */}
        <div className="col-lg-8">
          <div className="admin-card overflow-hidden">
            <div className="admin-card-header d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-bag-check me-2"></i>
                {t("customers.details.orders")}
              </span>
              <span className="badge bg-light text-dark fs-6">
                {customer.orders?.length || 0}
              </span>
            </div>

            <div className="card-body p-0">
              {customer.orders && customer.orders.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>{t("customers.details.address")}</th>
                        <th>{t("customers.details.total")}</th>
                        <th>{t("orderPrice.status") || t("customers.status")}</th>
                        <th>{t("customers.registeredAt")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customer.orders.map((order) => (
                        <tr
                          key={order.id}
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate(`/admin/home/orders/${order.id}`)}
                        >
                          <td className="fw-medium">#{order.id}</td>
                          <td>{order.address_details || "—"}</td>
                          <td className="fw-medium">
                            {(
                              Number(order.total_price || 0) +
                              Number(order.delivery_price || 0)
                            ).toFixed(2)}{" "}
                            TMT
                          </td>
                          <td>{order.status}</td>
                          <td>
                            {order.created_at
                              ? new Date(order.created_at).toLocaleDateString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-5 text-center text-muted">
                  <i className="bi bi-bag-x display-4 mb-3 d-block text-muted"></i>
                  {t("customers.details.noOrders")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
